import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type AlertType = 'success' | 'error' | 'info' | 'confirm';

type AlertContent = {
  title?: string;
  message: React.ReactNode;
};

type AlertOptions = AlertContent & {
  confirmText?: string;
  cancelText?: string;
  autoCloseMs?: number; // defaults to 10s for success
};

type InternalAlert = {
  id: number;
  type: AlertType;
  opts: AlertOptions;
  resolve: (value: boolean | void) => void;
};

type AlertAPI = {
  success: (message: React.ReactNode, opts?: Omit<AlertOptions, 'message'>) => Promise<void>;
  error:   (message: React.ReactNode, opts?: Omit<AlertOptions, 'message'>) => Promise<void>;
  info:    (message: React.ReactNode, opts?: Omit<AlertOptions, 'message'>) => Promise<void>;
  confirm: (opts: AlertOptions) => Promise<boolean>;
};

const AlertContext = createContext<AlertAPI | null>(null);
export const useAlert = (): AlertAPI => {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlert must be used within <AlertProvider>');
  return ctx;
};

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queueRef = useRef<InternalAlert[]>([]);
  const [current, setCurrent] = useState<InternalAlert | null>(null);
  const currentRef = useRef<InternalAlert | null>(null);
  const idRef = useRef(1);
  const lastFocusRef = useRef<HTMLElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // keep currentRef in sync
  useEffect(() => { currentRef.current = current; }, [current]);

  // lock scroll while open
  useEffect(() => {
    if (!current) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [current]);

  // One implementation with a union return
  const showImpl = useCallback(
    (type: AlertType, opts: AlertOptions): Promise<boolean | void> =>
      new Promise((resolve) => {
        const id = idRef.current++;
        queueRef.current.push({ id, type, opts, resolve });
        if (!currentRef.current) {
          const next = queueRef.current.shift() || null;
          if (next) setCurrent(next);
        }
      }),
    []
  );

  // API: coerce to exact return types, and depend on showImpl (no lint noise)
  const api: AlertAPI = useMemo(
    () => ({
      success: (message, opts = {}) =>
        showImpl('success', { message, ...opts }).then(() => undefined),
      error: (message, opts = {}) =>
        showImpl('error', { message, ...opts }).then(() => undefined),
      info: (message, opts = {}) =>
        showImpl('info', { message, ...opts }).then(() => undefined),
      confirm: (opts) =>
        showImpl('confirm', opts).then((v) => Boolean(v)),
    }),
    [showImpl]
  );

  // focus + auto-close (success defaults to 10s)
  useEffect(() => {
    if (!current) return;

    lastFocusRef.current = document.activeElement as HTMLElement | null;

    const t = setTimeout(() => { modalRef.current?.focus(); }, 0);

    const defaultAuto = current.type === 'success' ? 10_000 : undefined;
    const autoMs = current.opts.autoCloseMs ?? defaultAuto;
    if (autoMs) {
      autoTimerRef.current = setTimeout(() => { handleClose(false); }, autoMs);
    }

    return () => {
      clearTimeout(t);
      if (autoTimerRef.current) {
        clearTimeout(autoTimerRef.current);
        autoTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  const closeAndAdvance = (value: boolean | void) => {
    current?.resolve(value);
    setCurrent(null);

    if (lastFocusRef.current) {
      lastFocusRef.current.focus();
      lastFocusRef.current = null;
    }

    const next = queueRef.current.shift() || null;
    if (next) setTimeout(() => setCurrent(next), 10);
  };

  const handleConfirm = () => closeAndAdvance(true);
  const handleCancel  = () => closeAndAdvance(false);
  const handleClose   = (_: boolean | void) => closeAndAdvance(_);

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      if (current?.type === 'confirm') handleCancel();
      else handleClose(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!current) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      if (current.type === 'confirm') handleCancel();
      else handleClose(false);
      return;
    }
    if (e.key === 'Enter' && current.type === 'confirm') {
      e.preventDefault();
      handleConfirm();
      return;
    }
    if (e.key === 'Tab' && modalRef.current) {
      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
  };

  const styleByType: Record<
    AlertType,
    { icon: string; ring: string; header: string; button: string }
  > = {
    success: { icon: '✓', ring: 'ring-green-500',  header: 'text-green-700',  button: 'bg-green-600 hover:bg-green-700' },
    error:   { icon: '✕', ring: 'ring-red-500',    header: 'text-red-700',    button: 'bg-red-600 hover:bg-red-700' },
    info:    { icon: 'ℹ', ring: 'ring-blue-500',   header: 'text-blue-700',   button: 'bg-blue-600 hover:bg-blue-700' },
    confirm: { icon: '❓', ring: 'ring-yellow-500', header: 'text-yellow-700', button: 'bg-yellow-600 hover:bg-yellow-700' },
  };

  return (
    <AlertContext.Provider value={api}>
      {children}

      {current && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur"
          onMouseDown={onBackdropClick}
          aria-modal="true"
          role="dialog"
        >
          <div
            ref={modalRef}
            tabIndex={-1}
            onKeyDown={onKeyDown}
            className={[
              'focus:outline-none',
              'w-screen h-screen rounded-none',
              'md:w-[520px] md:h-auto md:rounded-2xl',
              'bg-white shadow-xl ring-2 p-5',
              styleByType[current.type].ring,
            ].join(' ')}
          >
            <div className="flex items-start gap-3">
              <div className="text-2xl" aria-hidden>{styleByType[current.type].icon}</div>
              <div className="flex-1">
                {current.opts.title && (
                  <h2 className={`text-lg font-semibold ${styleByType[current.type].header}`}>
                    {current.opts.title}
                  </h2>
                )}
                <div className="mt-1 text-slate-700">{current.opts.message}</div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              {current.type === 'confirm' ? (
                <>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700"
                    onClick={handleCancel}
                  >
                    {current.opts.cancelText ?? 'Cancel'}
                  </button>
                  <button
                    type="button"
                    className={`px-4 py-2 rounded-lg text-white ${styleByType[current.type].button}`}
                    onClick={handleConfirm}
                  >
                    {current.opts.confirmText ?? 'OK'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className={`px-4 py-2 rounded-lg text-white ${styleByType[current.type].button}`}
                  onClick={() => handleClose(undefined)}
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </AlertContext.Provider>
  );
};
